import { useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import styled from "styled-components";

import { gql } from "@examspark/api";
import { assertNativeError } from "@examspark/common";
import {
  Button,
  CautionBanner,
  Modal,
  ModalProps,
  SubmitButtons,
  Truncate,
  VSpace,
} from "@examspark/common-ui";
import { Resource } from "@examspark/types";

import { useQuerySearchClonedItems } from "../../../api/operations/items";
import { useAuthorizationContext } from "../../../hooks/useAuthorizationContext";

const StyledInProgress = styled.div`
  padding: 1.5rem 1rem;
  border-top: solid 1px #ddd;
`;
const StyledExistingCopies = styled(VSpace)`
  padding: 1.5rem 1rem;
  border-top: solid 1px #ddd;
`;
const StyledExistingCopiesHeadline = styled.div`
  color: #777;
`;
const StyledExistingItem = styled.div`
  border: solid 1px #ddd;
  border-radius: 0.5rem;
  padding: 1rem;
  display: flex;
  gap: 1rem;
  min-width: 0;
  background-color: #f7f7f7;
  align-items: center;
`;

const CopyItemBeforeEditConfirmModal = (
  props: ModalProps & {
    itemId?: string;
    targetBankId: string;
    onClone?: (newItem: Resource) => void;
  },
) => {
  const client = useQueryClient();
  const [isCloning, setIsCloning] = useState(false);
  const authorizationContext = useAuthorizationContext();
  const existingClonesQuery = useQuerySearchClonedItems({
    targetBankId: props.targetBankId,
    originResourceId: props.itemId
      ? [props.itemId, "item#v0"].join("/")
      : undefined,
    limit: 25,
  });

  const clonesExist =
    (existingClonesQuery.data?.pages[0]?.items?.length ?? 0) > 0;
  // console.log("clones", existingClonesQuery.data?.pages[0]?.items);
  const existingClones = existingClonesQuery.data?.pages[0]?.items;

  const handleClone = async () => {
    try {
      if (!props.itemId) return console.warn("No item ID to clone");
      setIsCloning(true);
      const newItemCreateResponse =
        await gql.operations.item.duplicateItemForCampfireUser(
          props.itemId,
          props.targetBankId,
          authorizationContext,
        );

      if (!newItemCreateResponse) {
        throw new Error("Failed to clone the item");
      }

      const result = await gql.operations.content.getItemDetails({
        id: newItemCreateResponse.id,
        sk: "item#v0",
      });

      if (!result) {
        throw new Error("Failed to fetch the cloned item");
      }

      props.onClone?.(result);
    } catch (e) {
      assertNativeError(e);
      console.error(e);
      alert(e.message);
    } finally {
      setIsCloning(false);
    }

    client.invalidateQueries(["cloned-items"]);
  };

  const handleSelectExisting = async (itemId: string) => {
    const result = await gql.operations.content.getItemDetails({
      id: itemId,
      sk: "item#v0",
    });

    if (!result) {
      throw new Error("Failed to fetch the cloned item");
    }

    props.onClone?.(result);
  };

  return (
    <Modal
      title="Caution"
      size="small"
      buttons={
        <SubmitButtons
          onCancel={props.onRequestClose}
          submitLabel="Make a Copy"
          isSubmitting={isCloning}
          isSubmitDisabled={existingClonesQuery.isFetching || clonesExist}
          onSubmit={handleClone}
        />
      }
      {...props}
    >
      <CautionBanner variant="plain">
        <p>
          Make a copy of this item to make changes. The original will remain
          available.
        </p>
      </CautionBanner>
      {existingClonesQuery.isFetching && (
        <StyledInProgress>Checking for copies...</StyledInProgress>
      )}
      {!existingClonesQuery.isFetching && clonesExist && (
        <StyledExistingCopies>
          <StyledExistingCopiesHeadline>
            Your other copies of this item:
          </StyledExistingCopiesHeadline>
          {existingClones?.map((item) => (
            <StyledExistingItem key={item.id}>
              <div style={{ minWidth: 0 }}>
                <Truncate>{item.name}</Truncate>
              </div>
              <div>
                <Button
                  title="Use"
                  variant="outlined"
                  color="primary"
                  onClick={() => handleSelectExisting(item.id)}
                >
                  Use
                </Button>
              </div>
            </StyledExistingItem>
          ))}
        </StyledExistingCopies>
      )}
    </Modal>
  );
};

export default CopyItemBeforeEditConfirmModal;
